import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "./DashboardLayout";
import { api, getErrorMessage } from "@/lib/api";
import { Sparkles, Zap, Loader2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import CheckoutModal from "./CheckoutModal";
import UpgradeModal from "./UpgradeModal";

const fmtChars = (n) => n >= 1000000 ? `${(n/1000000).toFixed(1).replace(/\.0$/,"")}M` : n >= 1000 ? `${(n/1000).toFixed(1).replace(/\.0$/,"")}k` : String(n);

const TOPUP_BUNDLES = [
    { id: "starter_mini",  label: "Mini",  articles: 50,  price: 249,  plans: ["starter","basic"] },
    { id: "starter_small", label: "Small", articles: 100, price: 499,  plans: ["starter","basic"] },
    { id: "starter_large", label: "Large", articles: 200, price: 999,  plans: ["starter","basic"] },
    { id: "premium_mini",  label: "Mini",  articles: 50,  price: 449,  plans: ["pro","business","professional"] },
    { id: "premium_small", label: "Small", articles: 100, price: 899,  plans: ["pro","business","professional"] },
    { id: "premium_large", label: "Large", articles: 200, price: 1799, plans: ["pro","business","professional"] },
];

export default function Billing() {
    const [data, setData] = useState(null);
    const [buyingBundle, setBuyingBundle] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [customAmount, setCustomAmount] = useState("");
    const [checkout, setCheckout] = useState(null); // modal state
    const [upgradeOpen, setUpgradeOpen] = useState(false);

    const load = () => api.get("/billing/usage").then(r => setData(r.data)).catch(() => {});

    useEffect(() => { load(); }, []);

    useEffect(() => {
        const id = setInterval(() => {
            load();
        }, 180000);
        return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Show toast on return from Paymently
    useEffect(() => {
        const payment = searchParams.get("payment");
        const type = searchParams.get("type");
        if (!payment) return;
        if (payment === "success") {
            const msg = type === "plan"
                ? "Plan upgraded successfully! Your new credits are ready."
                : "Credits added successfully! Your top-up is ready to use.";
            toast.success(msg);
            load();
        } else if (payment === "hold") {
            toast.info("Payment is on hold. We will check again automatically in a few minutes.");
            load();
        } else if (payment === "cancelled") {
            toast.info("Payment cancelled. No charges were made.");
        } else if (payment === "error") {
            const reason = searchParams.get("reason") || "unknown";
            toast.error(`Payment failed (${reason}). Please try again or contact support.`);
        }
        // Clean URL params
        setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!data) return (
        <div className="p-10 flex items-center gap-3 text-ink-muted">
            <Loader2 className="animate-spin" size={20}/> Loading billing…
        </div>
    );

    const plan = data.plan || "free";
    const pendingPlan = data.pending_plan || null;
    const pendingPlanAt = data.pending_plan_effective_at || null;
    const monthlyTotal = data.monthly_chars_total || 0;
    const monthlyUsed = Math.max(0, monthlyTotal - (data.monthly_chars_remaining || 0));
    const usagePct = monthlyTotal > 0 ? Math.min(100, Math.round((monthlyUsed / monthlyTotal) * 100)) : 0;
    const topupChars = data.topup_chars_remaining || 0;
    const charsLeft = (data.monthly_chars_remaining || 0) + topupChars;
    const holdPayments = data.hold_payments || [];

    const availableBundles = TOPUP_BUNDLES.filter(b => b.plans.includes(plan));

    const onBuyTopup = (bundleId) => {
        setCheckout({ type: "topup", bundle_id: bundleId });
    };

    const onBuyCustom = () => {
        const amt = parseInt(customAmount, 10);
        if (!amt || amt < 50) { toast.error("Minimum custom top-up is ৳50"); return; }
        if (amt > 100000) { toast.error("Maximum custom top-up is ৳1,00,000"); return; }
        const rate = ["pro","professional","business"].includes(plan) ? 111 : 200;
        setCheckout({ type: "custom", amount: amt, chars_per_bdt: rate });
    };

    return (
        <div data-testid="billing-page">
            {checkout && (
                <CheckoutModal
                    checkout={checkout}
                    onClose={() => setCheckout(null)}
                    onSuccess={() => { setCheckout(null); load(); setCustomAmount(""); }}
                />
            )}
            <UpgradeModal open={upgradeOpen} onClose={() => { setUpgradeOpen(false); load(); }} />
            <PageHeader
                eyebrow="Billing" title="Credits & plan."
                subtitle="Track usage, top up credits, or switch plans. Pay securely via bKash, Nagad, or card."
                action={
                    <button onClick={() => setUpgradeOpen(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink hover:bg-coral text-cream-100 text-[13px] font-semibold transition">
                        <Sparkles size={13}/> Upgrade / Renew plan
                    </button>
                }
            />

            {holdPayments.length > 0 && (
                <div className="mb-8 bg-amber-50 border border-amber-200 rounded-3xl p-5">
                    <div className="flex items-center justify-between gap-3 mb-3">
                        <h3 className="font-display text-[24px] font-semibold tracking-tight text-amber-900">Payments on hold</h3>
                        <span className="text-[12px] text-amber-800">Auto-checks every few minutes</span>
                    </div>
                    <div className="space-y-2">
                        {holdPayments.map((p) => (
                            <div key={p.intent_id} className="bg-white rounded-xl border border-amber-200 px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px]">
                                <span className="font-mono text-amber-900">{p.intent_id}</span>
                                <span className="text-ink capitalize">{p.type}</span>
                                <span className="text-ink">৳{(p.amount_bdt || 0).toLocaleString()}</span>
                                <span className="text-amber-800 uppercase font-semibold">{(p.gateway_status || p.status || "PENDING")}</span>
                                <span className="text-ink-muted">Created {p.created_at ? new Date(p.created_at).toLocaleString("en-BD") : "-"}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Credit overview */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
                <div className="lg:col-span-2 bg-white rounded-3xl p-7 border border-cream-300 shadow-soft">
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted">Monthly credits</p>
                            <h3 className="font-display text-[28px] font-semibold tracking-tight capitalize mt-1">{plan} plan</h3>
                        </div>
                        <span className="text-[12px] font-mono text-ink-muted">Renews {new Date(data.renewal_date).toLocaleDateString("en-BD")}</span>
                    </div>
                    <p className="text-[12px] text-ink-muted mb-3">Started {data.plan_started_at ? new Date(data.plan_started_at).toLocaleDateString("en-BD") : "-"}</p>
                    {pendingPlan && (
                        <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
                            Plan change scheduled: <strong className="capitalize">{pendingPlan}</strong>
                            {pendingPlanAt ? ` on ${new Date(pendingPlanAt).toLocaleDateString("en-BD")}` : " at next renewal"}.
                        </div>
                    )}
                    <div className="flex items-baseline gap-2 mb-3">
                        <span className="font-display text-[48px] font-bold leading-none">{fmtChars(monthlyUsed)}</span>
                        <span className="text-[15px] text-ink-muted">/ {fmtChars(monthlyTotal)} chars used this period</span>
                    </div>
                    <div className="h-3 bg-cream-300 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${usagePct >= 90 ? "bg-red-500" : usagePct >= 70 ? "bg-amber-400" : "bg-coral"}`} style={{width: `${usagePct}%`}}/>
                    </div>
                    <p className="text-[12px] text-ink-muted mt-3">{usagePct}% used · <strong>{fmtChars(data.monthly_chars_remaining || 0)} monthly chars left</strong> (~{Math.floor((data.monthly_chars_remaining||0)/1000)} articles)</p>
                    {topupChars > 0 && (
                        <div className="mt-4 flex items-center gap-2 text-[13px] text-ink bg-mint/20 rounded-xl px-4 py-2.5 w-fit">
                            <Zap size={13} className="text-mint"/>
                            <span>+{fmtChars(topupChars)} top-up chars banked · <strong>{fmtChars(charsLeft)} total available</strong></span>
                        </div>
                    )}
                </div>

                <div className="bg-coral text-white rounded-3xl p-7 relative overflow-hidden">
                    <Sparkles size={20} className="mb-4 opacity-90"/>
                    <p className="text-[11px] uppercase tracking-[0.22em] font-semibold opacity-90">Monthly bill</p>
                    <p className="font-display text-[44px] font-bold mt-2 leading-none">৳{(data.amount_bdt || 0).toLocaleString()}</p>
                    <p className="text-[13px] opacity-80 mt-2">/month</p>
                    <div className="mt-6 flex items-center gap-2 text-[12px] opacity-70">
                        <CreditCard size={13}/>
                        <span>Pay via bKash / Nagad / Rocket / card</span>
                    </div>
                </div>
            </div>

            {/* Top-up credit bundles */}
            <div className="mb-10">
                <div className="flex items-center justify-between mb-5">
                    <h3 className="font-display text-[28px] font-semibold tracking-tight">Buy top-up credits</h3>
                    <span className="text-[12px] text-ink-muted bg-cream-100 border border-cream-300 rounded-full px-3 py-1">Credits roll over · No expiry while subscribed</span>
                </div>
                {availableBundles.length === 0 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 text-[14px] text-amber-800">
                        Top-up credits are only available on paid plans. Upgrade to Starter or above.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {availableBundles.map(b => (
                            <div key={b.id} className="bg-white rounded-3xl p-6 border border-cream-300 shadow-soft hover:shadow-soft-lg transition-all">
                                <div className="flex items-start justify-between mb-4">
                                    <div>
                                        <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted">{b.label} pack</p>
                                        <p className="font-display text-[36px] font-bold leading-none mt-1">{b.articles}</p>
                                        <p className="text-[13px] text-ink-muted">articles · {(b.articles * 1000).toLocaleString()} chars</p>
                                    </div>
                                    <Zap size={18} className="text-coral mt-1"/>
                                </div>
                                <p className="font-display text-[22px] font-semibold mb-4">৳{b.price.toLocaleString()}</p>
                                <button
                                    onClick={() => onBuyTopup(b.id)}
                                    disabled={!!buyingBundle}
                                    className="w-full rounded-full py-2.5 text-[13px] font-semibold bg-ink hover:bg-coral text-cream-100 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {buyingBundle === b.id ? <><Loader2 size={13} className="animate-spin"/> Adding…</> : "Buy credits"}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                <p className="text-[12px] text-ink-muted mt-3">⚡ Credits are applied immediately after payment is confirmed.</p>
            </div>

            {/* Custom amount top-up */}
            {plan !== "free" && (
                <div className="mb-10">
                    <h3 className="font-display text-[28px] font-semibold tracking-tight mb-2">Custom top-up</h3>
                    <p className="text-[13px] text-ink-muted mb-5">Enter any amount between ৳50 – ৳1,00,000 and see exactly how many characters you'll receive.</p>
                    <div className="bg-white rounded-3xl p-6 border border-cream-300 shadow-soft max-w-md">
                        <label className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted mb-2">Amount (BDT ৳)</label>
                        <div className="flex gap-3">
                            <input
                                type="number"
                                min="50"
                                max="100000"
                                step="50"
                                value={customAmount}
                                onChange={e => setCustomAmount(e.target.value)}
                                onKeyDown={e => e.key === "Enter" && onBuyCustom()}
                                placeholder="e.g. 500"
                                className="flex-1 border border-cream-300 rounded-xl px-4 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-coral/40"
                            />
                            <button
                                onClick={onBuyCustom}
                                className="rounded-full px-5 py-2.5 text-[13px] font-semibold bg-ink hover:bg-coral text-cream-100 transition-colors"
                            >
                                Buy
                            </button>
                        </div>
                        {customAmount && parseInt(customAmount, 10) >= 50 && (
                            <p className="mt-3 text-[13px] text-ink-muted">
                                ৳{parseInt(customAmount, 10).toLocaleString()} →{" "}
                                <strong className="text-ink">
                                    {((parseInt(customAmount, 10) || 0) * (["pro","professional","business"].includes(plan) ? 111 : 200)).toLocaleString()} characters
                                </strong>{" "}
                                (~{Math.floor(((parseInt(customAmount, 10) || 0) * (["pro","professional","business"].includes(plan) ? 111 : 200)) / 1000)} articles)
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Purchase history */}
            <h3 className="font-display text-[28px] font-semibold tracking-tight mb-5">Purchase history</h3>
            <div className="bg-white rounded-3xl border border-cream-300 shadow-soft overflow-hidden">
                {!data.invoices || data.invoices.length === 0 ? (
                    <div className="p-10 text-center text-ink-muted text-[14px]">No purchases yet.</div>
                ) : (
                    <table className="w-full">
                        <thead className="bg-cream-100">
                            <tr className="text-left">
                                <th className="px-6 py-3 text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted">ID</th>
                                <th className="px-4 py-3 text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted">Date</th>
                                <th className="px-4 py-3 text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted">Type</th>
                                <th className="px-4 py-3 text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted">Amount</th>
                                <th className="px-4 py-3 text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-cream-300">
                            {data.invoices.map(inv => (
                                <tr key={inv.id} className="hover:bg-cream-100">
                                    <td className="px-6 py-3 text-[12px] font-mono text-ink">{inv.id}</td>
                                    <td className="px-4 py-3 text-[13px] text-ink-muted">{inv.date}</td>
                                    <td className="px-4 py-3 text-[13px] text-ink capitalize">{inv.type || "top-up"}</td>
                                    <td className="px-4 py-3 text-[13px] font-mono">৳{(inv.amount_bdt || 0).toLocaleString()}</td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-full ${
                                            inv.status === "active" || inv.status === "paid" ? "bg-mint/30 text-ink" : "bg-amber-100 text-amber-700"
                                        }`}>
                                            <span className={`w-1.5 h-1.5 rounded-full ${inv.status === "active" || inv.status === "paid" ? "bg-mint" : "bg-amber-400"}`}/>
                                            {inv.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

